// packages/ui/react/Modal.jsx
//
// 🔴 RUN CT — MOVED HERE FROM `apps/main/client/src/chrome/Modal.jsx`, WITH ITS TWO CONSUMERS.
// It had exactly two importers (Clark's AuthorityModal and DocCheck); AuthorityModal was promoted
// into this package so Clark and Research render ONE modal instead of two lookalikes, and the
// primitive came with it rather than being reimplemented on this side. Both consumers now import
// it from here, so there is still exactly one portal, one blur value and one Esc handler.
//
// Shared blurred-blue modal primitive — replicates SignupGate's .ccv2-gate-* chrome
// (blurred navy backdrop + navy-header card) with its own cc-modal-* classes. Portaled
// to <body>.
//
// Props:
//   open        — render when truthy.
//   title       — node rendered in the navy header.
//   icon        — optional node rendered above the title in the header.
//   children    — body content.
//   onClose     — called on dismiss (Esc / backdrop) when dismissable.
//   dismissable — default true. When true: Esc + backdrop-click call onClose. When
//                 false: forced choice — no Esc, no backdrop dismiss (backdrop still
//                 renders + blurs, it just doesn't close).
//
// NOTE: SignupGate is deliberately NOT migrated onto this yet (separate follow-up);
// mild value duplication is accepted.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";

/* 🔴 RUN CT — THE FOCUS TRAP, AND IT IS A GAP THAT WAS MEASURED RATHER THAN ASSUMED CLOSED.
 *
 * This primitive shipped with Esc and a backdrop click and NOTHING ELSE. `aria-modal="true"` was on
 * the card, which TELLS a screen reader the rest of the page is inert while doing nothing to make it
 * so: Tab walked straight out of the dialog into the page behind the blur, where every control was
 * still reachable and none of it was visible. Focus was never moved INTO the dialog on open either,
 * so a keyboard user pressing Tab first landed somewhere underneath it.
 *
 * ⭐ IT IS BUILT HERE, IN THE PRIMITIVE, FOR THE REASON THE PRIMITIVE EXISTS. RUN CT promoted
 * AuthorityModal into this package specifically so Clark and Research could not drift; a trap
 * written in the authority modal alone would have been a third behaviour, and DocCheck — the other
 * consumer — would have kept the old one. One implementation, both consumers, one behaviour.
 *
 * ⚠️ DOCCHECK'S MODAL CHANGES TOO, AND THAT IS STATED RATHER THAN SLIPPED IN. It gains the trap, the
 * initial focus and the restore. That is a fix in both places, but it IS a behaviour change to a
 * surface RUN CT was not asked to touch, and the report says so.
 *
 * 🔴 THE RESTORE READS THE ELEMENT AT OPEN TIME, NOT AT CLOSE TIME. `document.activeElement`
 * once the dialog is focused is the dialog — so capturing it on close would return focus to a node
 * that is being unmounted, i.e. to `<body>`, which is indistinguishable from no restore at all.
 * Captured on the OPEN transition, restored on teardown.
 *
 * ⚠️ AND THE RESTORE IS GUARDED ON `isConnected`. A card that opened this dialog can legitimately be
 * gone by the time it closes (the list re-rendered, the answer was replaced). Calling `.focus()` on a
 * detached node silently does nothing and leaves focus on `<body>`; checking first lets the guard be
 * the honest no-op rather than pretending the restore happened. */
const FOCUSABLE = [
  "a[href]", "area[href]", "button:not([disabled])", "input:not([disabled])",
  "select:not([disabled])", "textarea:not([disabled])", "iframe", "object", "embed",
  "[contenteditable]", "[tabindex]",
].join(",");

/** Every tabbable node inside `root`, in document order. Negative tabindex is focusable, not tabbable. */
function tabbables(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (el.getAttribute("tabindex") !== null && Number(el.getAttribute("tabindex")) < 0) return false;
    if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") return false;
    // offsetParent is null for display:none; a modal is never position:fixed inside itself.
    return el.offsetParent !== null || el === document.activeElement;
  });
}

export default function Modal({ open, title, icon, children, onClose, dismissable = true }) {
  const cardRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!open || !dismissable) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissable, onClose]);

  /* Focus: capture the invoker, move focus in, trap Tab, restore on close. Esc stays above so a
     non-dismissable dialog still traps (a forced choice must not be escapable BY TABBING OUT). */
  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;

    const card = cardRef.current;
    /* The first control, or the card itself. The card carries tabIndex={-1} so it is focusable
       without being tabbable — a dialog whose only content is text still takes focus. */
    const first = tabbables(card)[0];
    (first || card)?.focus?.();

    const onTab = (e) => {
      if (e.key !== "Tab" || !card) return;
      const list = tabbables(card);
      if (list.length === 0) { e.preventDefault(); card.focus?.(); return; }
      const firstEl = list[0];
      const lastEl = list[list.length - 1];
      /* ⚠️ THE OUT-OF-DIALOG CASE IS HANDLED EXPLICITLY. If focus has somehow left the card
         (a click on the backdrop, a script moving it), wrapping first/last is not enough — Tab from
         outside would continue through the page. Pull it back in. */
      if (!card.contains(document.activeElement)) { e.preventDefault(); firstEl.focus(); return; }
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener("keydown", onTab, true);

    return () => {
      document.removeEventListener("keydown", onTab, true);
      const back = restoreRef.current;
      restoreRef.current = null;
      if (back && back.isConnected && typeof back.focus === "function") back.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="cc-modal-overlay" onClick={() => dismissable && onClose && onClose()}>
      <div
        className="cc-modal-card"
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "cc-modal-title" : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {(icon || title) && (
          <div className="cc-modal-header">
            {icon && <div className="cc-modal-icon" aria-hidden="true">{icon}</div>}
            {title && <h3 className="cc-modal-title" id="cc-modal-title">{title}</h3>}
          </div>
        )}
        <div className="cc-modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
}
